/**
 * Google Geocoding API integration for import rows missing coordinates.
 */

import type { AddressConfig, GeocodeFailure } from "./types";

interface GeocodingResult {
  lat: number;
  lng: number;
  formattedAddress: string;
}

export interface GeocodedRow {
  rowIndex: number;
  lat: number;
  lng: number;
  address: string;
}

export interface GeocodedBatchResult {
  geocodedRows: GeocodedRow[];
  failures: GeocodeFailure[];
}

/**
 * Build an address string from a row using the configured address columns.
 */
export function buildAddress(
  row: Record<string, string>,
  config: AddressConfig,
): string {
  const parts: string[] = [];

  if (config.addressColumn) {
    const val = row[config.addressColumn]?.trim();
    if (val) parts.push(val);
  }
  if (config.cityColumn) {
    const val = row[config.cityColumn]?.trim();
    if (val) parts.push(val);
  }

  // State and zip go together: "State ZIP"
  const state = config.stateColumn ? row[config.stateColumn]?.trim() : "";
  const zip = config.zipColumn ? row[config.zipColumn]?.trim() : "";
  const stateZip = [state, zip].filter(Boolean).join(" ");
  if (stateZip) parts.push(stateZip);

  return parts.join(", ");
}

/**
 * Geocode a single address via Google Geocoding API.
 */
export async function geocodeAddress(
  address: string,
  apiKey: string,
): Promise<GeocodingResult | null> {
  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;
  const res = await fetch(url);

  if (!res.ok) {
    throw new Error(`Geocoding API HTTP error: ${res.status}`);
  }

  const data = (await res.json()) as {
    status: string;
    results: Array<{
      geometry: { location: { lat: number; lng: number } };
      formatted_address: string;
    }>;
    error_message?: string;
  };

  if (data.status === "ZERO_RESULTS") {
    return null;
  }

  if (data.status !== "OK") {
    throw new Error(
      data.error_message ?? `Geocoding API error: ${data.status}`,
    );
  }

  const result = data.results[0];
  if (!result) return null;

  return {
    lat: result.geometry.location.lat,
    lng: result.geometry.location.lng,
    formattedAddress: result.formatted_address,
  };
}

/**
 * Geocode a batch of rows sequentially.
 * Only rows missing coordinates are geocoded.
 */
export async function geocodeBatch(
  rows: { row: Record<string, string>; rowIndex: number }[],
  addressConfig: AddressConfig,
  apiKey: string,
  onProgress?: (current: number, total: number, lastAddress: string) => void,
): Promise<GeocodedBatchResult> {
  const geocodedRows: GeocodedRow[] = [];
  const failures: GeocodeFailure[] = [];
  const total = rows.length;

  for (let i = 0; i < rows.length; i++) {
    const { row, rowIndex } = rows[i]!;
    const address = buildAddress(row, addressConfig);

    if (!address) {
      failures.push({
        row: rowIndex + 2, // 1-indexed, skip header
        address: "(empty)",
        reason: "No address fields available",
      });
      onProgress?.(i + 1, total, "(empty)");
      continue;
    }

    try {
      const result = await geocodeAddress(address, apiKey);

      if (result) {
        geocodedRows.push({
          rowIndex,
          lat: result.lat,
          lng: result.lng,
          address: result.formattedAddress,
        });
      } else {
        failures.push({
          row: rowIndex + 2,
          address,
          reason: "No results found",
        });
      }
    } catch (err) {
      failures.push({
        row: rowIndex + 2,
        address,
        reason: `API error: ${err instanceof Error ? err.message : String(err)}`,
      });
    }

    onProgress?.(i + 1, total, address);
  }

  return { geocodedRows, failures };
}
